import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/** Available predefined color themes for the progress bar. */
export type EvProgressBarTheme = 'blue' | 'grey' | 'green';

/**
 * A minimal, customizable progress bar component.
 *
 * @example
 * 
 * ```html
 * <!-- Basic usage with theme -->
 * <div style="width: 200px; height: 20px;">
 *       <ev-progress-bar [progress]="0.75" theme="green"></ev-progress-bar>
 * </div>
 * ```
 * 
 * external CSS can style the component:
 * 
 * ```scss
 * ev-progress-bar {
 *     margin: 10px;
 *     height: 16px;
 *     width: 200px;
 * }
 * ```
 * 
 * ```html
 * <!-- specific height/width -->
 * <div style="width: 500px;">
 *       <ev-progress-bar width="200px" height="16px" [progress]="0.75" theme="green"></ev-progress-bar>
 *       <ev-progress-bar width="200px" height="16px" [progress]="0.75" theme="green"></ev-progress-bar>
 * </div>
 *
 * <!-- Custom colors -->
 * <ev-progress-bar [progress]="0.5" backgroundColor="#eee" barColor="linear-gradient(to bottom, #fc0, #f60)"></ev-progress-bar>
 *
 * <!-- Right-to-left fill -->
 * <ev-progress-bar [progress]="0.9" direction="right" />
 * ```
 * 


 */
@Component({
    selector: 'ev-progress-bar',
    templateUrl: './ev-progress-bar.component.html',
    styleUrl: './ev-progress-bar.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[style.width]': 'width()',
        '[style.height]': 'height()',
        '[class]': 'themeClass()',
    },
})
export class EvProgressBarComponent {
    /**
     * Progress value between 0 and 1
    */
    readonly progress = input(0);

    readonly theme = input<EvProgressBarTheme>('blue');
    readonly backgroundColor = input<string | null>(null);
    readonly barColor = input<string | null>(null);
    readonly direction = input<'left' | 'right'>('left');
    readonly width = input<string | null>(null);
    readonly height = input<string | null>(null);

    protected readonly progressPercent = computed(() => {
        const progress = Math.round(this.progress() * 100);
        return Math.max(0, Math.min(100, progress));
    });

    protected readonly themeClass = computed(() =>
        this.backgroundColor() || this.barColor() ? '' : this.theme()
    );
}
